import { useEffect, useRef, useState } from 'react'

/** Lo que tarda la cifra en llegar a su valor. La barra de avance usa 700. */
const DURACION_MS = 1_200

/** Ease-out cúbica: arranca rápido y frena, como en el recetario. */
const suavizar = (t: number) => 1 - Math.pow(1 - t, 3)

/**
 * A-22 · Contador.
 *
 * La cifra sube desde cero cuando la tarjeta entra en pantalla, no al cargar:
 * un número que ya está puesto se lee como dato estático, uno que sube se lee
 * como medición.
 *
 * Devuelve el valor a pintar y la ref de la tarjeta que hay que observar.
 */
export function useContador<T extends HTMLElement = HTMLDivElement>(
  destino: number,
  duracionMs: number = DURACION_MS,
) {
  const [nodo, setNodo] = useState<T | null>(null)
  const [valor, setValor] = useState(0)
  const yaCorrio = useRef(false)

  useEffect(() => {
    if (!nodo) return

    // Sin observador o con movimiento reducido, la cifra se pone y ya.
    const sinMovimiento = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (typeof IntersectionObserver === 'undefined' || sinMovimiento) {
      setValor(destino)
      return
    }

    // Si el dato cambia después (un refresh del dashboard), la cifra se
    // actualiza sin volver a contar: contar de nuevo se siente como un error.
    if (yaCorrio.current) {
      setValor(destino)
      return
    }

    let cuadro = 0
    const observador = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          if (!entrada.isIntersecting) continue
          observador.unobserve(entrada.target)
          yaCorrio.current = true

          const inicio = performance.now()
          const paso = (ahora: number) => {
            const t = Math.min((ahora - inicio) / duracionMs, 1)
            setValor(Math.round(destino * suavizar(t)))
            if (t < 1) cuadro = requestAnimationFrame(paso)
          }
          cuadro = requestAnimationFrame(paso)
        }
      },
      { threshold: 0.4 },
    )

    observador.observe(nodo)

    // Misma razon que en useRevelado: en segundo plano el observador no
    // dispara y la cifra se quedaria en cero.
    const alVolver = () => {
      if (document.hidden || yaCorrio.current) return
      observador.unobserve(nodo)
      observador.observe(nodo)
    }
    document.addEventListener('visibilitychange', alVolver)

    return () => {
      document.removeEventListener('visibilitychange', alVolver)
      observador.disconnect()
      cancelAnimationFrame(cuadro)
    }
  }, [nodo, destino, duracionMs])

  return { ref: setNodo, valor }
}
